import { DesktopNotificationDetails } from '@weave/schema-gen-ts/dist/schemas/notification/v1/notification_api.pb';
import { CallIntelligenceUpdate } from '@weave/schema-gen-ts/dist/shared/call-intelligence/notification/message.pb';
import { DeviceConnectionAlert } from '@weave/schema-gen-ts/dist/shared/notification/alerts.pb';
import {
  CallRecordTagNotification as CallRecordTagNotificationType,
  MissedCallNotification as MissedCallNotificationType,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/message.pb';
import {
  VoicemailTagsAlertEvent as VoicemailTagNotificationType,
  VoicemailMessageAlertEvent,
} from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import type { Prettify } from '../prettify';

export type WeavePopNotification = DistributiveOmit<WeavePopNotificationWithActions, 'actions'>;
export type GetWeavePopNotificationByType<T extends WeavePopNotification['type']> = Prettify<
  Extract<WeavePopNotification, { type: T }>
>;
export type GetWeavePopNotificationWithActionsByType<T extends WeavePopNotificationWithActions['type']> = Extract<
  WeavePopNotificationWithActions,
  { type: T }
>;
export type GetWeavePopNotificationActionsByType<T extends WeavePopNotification['type']> =
  GetWeavePopNotificationWithActionsByType<T>['actions'];

export type GetWeavePopNotificationActionByType<
  T extends WeavePopNotification['type'],
  A extends GetWeavePopNotificationActionsByType<T>['action']
> = Extract<GetWeavePopNotificationWithActionsByType<T>['actions'], { action: A }>;

type WeavePopNotificationWithActions =
  | DeviceConnectionChangeNotification
  | MissedCallNotification
  | PhoneNotification
  | CallRecordTagNotification
  | ChatMessageNotification
  | FaxNotification
  | FaxTagNotification
  | MessageFollowupNotification
  | ReviewNotification
  | SmsMessageNotification
  | ScheduleRequestNotification
  | ScheduleWaitlistNotification
  | SmsTagNotification
  | InboxRobocallTagNotification
  | FormSubmissionNotification
  | PaymentsNotification
  | PreferenceNotification
  | TaskCenterNotification
  | VoicemailTagNotification
  | VoicemailNotification
  | UnhappyCallNotification
  | TaskCenterAiNotification
  | AiReceptionistStateNotification;

export type WeavePopNotificationActions = WeavePopNotificationWithActions['actions']['action'];

type DistributiveOmit<T, K extends keyof any> = T extends any ? Omit<T, K> : never;

type BaseNotification = {
  id: string;
  timestamp: string | number;
  location?: string;
  state: {
    status: 'read' | 'unread';
    paused: boolean;
    timeout?: number; //in ms
  };
};

export type NotificationActions = {
  label: string;
  action: () => void;
  variant?: 'primary' | 'secondary';
  Icon?: React.FC<any>;
}[];

export type BaseActions =
  | { action: 'view'; payload: undefined }
  | { action: 'ignore'; payload: undefined }
  | { action: 'mark-as-read'; payload: undefined }
  | { action: 'timed-out'; payload: undefined }
  | { action: 'focus'; payload: undefined }
  | { action: 'blur'; payload: undefined }
  | NotificationPreferenceAction;

type DeviceConnectionChangeNotification = BaseNotification & {
  type: 'device-connection';
  payload: DeviceConnectionAlert & {
    activeUserName?: string;
    deviceName?: string;
  };
  actions: BaseActions;
  flags?: never;
};

type MissedCallNotification = BaseNotification & {
  type: 'missed-call';
  payload: MissedCallNotificationType;
  actions: BaseActions;
  flags?: never;
};

type PhoneNotification = BaseNotification & {
  type: 'incoming-call';
  payload: {
    callerName: string;
    callerNumber: string;
    recipientLocationName: string;
  };
  actions: BaseActions;
  flags?: never;
};

type CallRecordTagNotification = BaseNotification & {
  type: 'call-record-tag';
  payload: CallRecordTagNotificationType;
  actions: BaseActions;
  flags?: never;
};

type VoicemailNotification = BaseNotification & {
  type: 'voicemail-message';
  payload: VoicemailMessageAlertEvent;
  actions: BaseActions;
  flags?: never;
};

export type VoicemailTagNotification = BaseNotification & {
  type: 'voicemail-tag';
  payload: VoicemailTagNotificationType;
  actions: BaseActions;
  flags?: never;
};

type ChatMessageNotification = BaseNotification & {
  type: 'chat-message-new';
  payload: {
    authorName: string | null;
    message: string;
    channelId: string;
    isMention: boolean; //content of the mention: eg name of the person mentioned
    reaction: string | null; //type of the reaction like :thumbsup
    attachments:
      | {
          type: 'image' | 'file' | 'video' | 'giphy';
        }[]
      | null;
  };

  actions: BaseActions;
  flags?: never;
};

type TaskCenterNotification = BaseNotification & {
  type: 'task-center';
  payload: NonNullable<DesktopNotificationDetails['taskAlert']>;
  actions: BaseActions;
  flags?: never;
};

type TaskCenterAiNotification = BaseNotification & {
  type: 'task-center-ai-settings';
  payload: NonNullable<DesktopNotificationDetails['taskAlert']>;
  actions: BaseActions;
  flags?: never;
};

type FaxNotification = BaseNotification & {
  type: 'fax-new';
  payload: {
    faxId: string;
    action: 'inbound' | 'status';
    from: string;
    to: string;
  };
  actions: BaseActions;
  flags?: never;
};

type FaxTagNotification = BaseNotification & {
  type: 'fax-tag';
  payload: NonNullable<DesktopNotificationDetails['faxTagAlert']>;
  actions: BaseActions;
  flags?: never;
};

type MessageFollowupNotification = BaseNotification & {
  type: 'message-followup';
  payload: {
    method: 'call' | 'email' | 'text';
    patient: {
      first_name: string;
      last_name: string;
      id: string;
      phone_number: string;
      email: string;
    };
    text: string;
  };
  actions: BaseActions;
  flags?: never;
};

type ReviewNotification = BaseNotification & {
  type: 'review-new';
  payload: {
    title: string;
    body: string;
    siteName: string;
    reviewId: string;
    author: string;
    rating: number;
    recommendationType: string;
  };
  actions: BaseActions;
  flags?: never;
};

export type SmsMessageNotification = BaseNotification & {
  type: 'sms-message-new';
  payload: {
    body: string;
    number: string;
    personId: string;
    threadId: string;
    departmentId: string;
    locationPhone: string;
    locationPhoneDisplayName?: string;
    locationId: string;
    firstName: string;
    lastName: string;
    responder?: string;
    tags?: {
      [key: string]: {
        createdAt: string;
        createdBy: string;
        id: string;
        locationId: string;
        taggedBy?: string;
        name: string;
        assignedUserIds?: string[];
      };
    };
    notificationID?: string;
    threadStatus?: string;
  };
  actions: BaseActions | { action: 'respond'; payload: string };
  flags: {
    shouldAllowReply: boolean;
  };
};

export type WeaveNotificationActions = BaseActions | { action: 'respond'; payload: string };

export type ScheduleRequestNotification = BaseNotification & {
  type: 'schedule-request-new';
  payload: {
    appointmentType: string;
    dateTime: string;
    id: string;
    durationMinutes: number;
    note: string;
    notificationType: 'ScheduleRequest';
    // this is on the websocket payload, but we do not display it in the notification
    schedulee?: {
      firstName: string;
      lastName: string;
      phoneNumber?: string;
      email?: string;
      WeavePersonID?: string;
    };
  };
  actions: BaseActions;
  flags?: never;
};

export type ScheduleWaitlistNotification = BaseNotification & {
  type: 'schedule-waitlist';
  payload: NonNullable<DesktopNotificationDetails['waitlistAlert']>;
  actions: BaseActions;
  flags?: never;
};

type SmsTagNotification = BaseNotification & {
  type: 'sms-tag';
  payload: NonNullable<DesktopNotificationDetails['desktopSmsTagV2Alert']>;
  actions: BaseActions;
  flags?: never;
};

type InboxRobocallTagNotification = BaseNotification & {
  type: 'inbox-robocall-tag';
  payload: NonNullable<DesktopNotificationDetails['robocallTagAlert']>;
  actions: BaseActions;
  flags?: never;
};

type AiReceptionistStateNotification = BaseNotification & {
  type: 'ai-receptionist-state';
  payload: NonNullable<DesktopNotificationDetails['aiReceptionistStateAlert']>;
  actions: BaseActions;
  flags?: never;
};

type FormSubmissionNotification = BaseNotification & {
  type: 'form-submission-new';
  payload: {
    locationId?: string;
    submissionId: string;
    firstName: string;
    lastName: string;
    documentName: string;
    submittedAt: string;
  };
  actions: BaseActions;
  flags?: never;
};

export type PaymentsNotification = BaseNotification & {
  type: 'payments';
  payload: {
    invoiceId?: string;
    origin?: string;
    patientName?: string;
    originLabel?: string;
  };
  actions: BaseActions;
  flags?: never;
};

type UnhappyCallNotification = BaseNotification & {
  type: 'unhappy-call';
  payload: CallIntelligenceUpdate;
  actions: BaseActions;
  flags?: never;
};

//---------- Notification Preference Types ----------

export type PreferenceType = 'pause-notifications' | 'auto-pause-notifications-reminder';

type PreferenceActionTypes = 'pause' | 'dismiss' | 'unPause';

type NotificationPreferenceAction = {
  action: 'preference';
  payload: {
    actionType: PreferenceActionTypes;
  };
};

export type PreferenceNotification = BaseNotification & {
  type: 'preference';
  payload: {
    title: string;
    body: string;
    notificationType: WeavePopNotification['type'];
  };
  actions: BaseActions;
  preferenceType: PreferenceType;
};

export const PopupDuration = {
  Manual: 0,
  FourThousand: 4000,
  TenThousand: 10000,
} as const;
type PopupDurationKeys = keyof typeof PopupDuration;
export type PopupDurationValues = (typeof PopupDuration)[PopupDurationKeys];

type Placement = 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';

export interface TransientQueueProps {
  placement?: Placement;
}

type NotificationSettings = {
  durationMs: PopupDurationValues;
  placement: TransientQueueProps['placement'];
};

export type SoundId =
  | 'badadading'
  | 'messages-notification-audio-bell'
  | 'messages-notification-audio-chime'
  | 'messages-notification-audio-crystal'
  | 'messages-notification-audio-deep'
  | 'chat-notification-direct'
  | 'messages-notification-audio-double-ding'
  | 'chat-notification-group'
  | 'messages-notification-audio-keyboard'
  | 'messages-notification-audio-marimba'
  | 'messages-notification-audio-pop-ding'
  | 'messages-notification-audio-success'
  | 'messages-notification-audio-up-and-down'
  | 'messages-notification-audio-xylophone';

export type SoundSettings = Record<WeavePopNotification['type'], SoundId | null>;
export interface NotificationSettingsStore {
  notificationSettings: NotificationSettings;
  notificationSounds: SoundSettings;
  manageAll: boolean;
  hasChangedDuration: boolean;
}
